// pages/hr/wizard/steps/ReviewStep.tsx
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemContent,
  Alert,
} from '@mui/joy';
import {
  User as PersonOutlined,
  Briefcase as WorkOutlined,
  Bank as AccountBalanceOutlined,
  FileText as DescriptionOutlined,
  CheckCircle as CheckCircleOutlined,
} from '@phosphor-icons/react';
import type { WizardStepProps } from '../../../../types/hr';

// Type helpers for step data
interface PersonalDetailsData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  date_of_birth?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  emergency_contact_name?: string;
}

interface EmploymentDetailsData {
  job_title?: string;
  department?: string;
  start_date?: string;
  has_direct_deposit?: boolean;
}

interface TaxInfoData {
  filing_status?: string;
  dependents_amount?: string;
  extra_withholding?: string;
  tax_classification?: string;
  tin_type?: string;
}

interface DocumentsData {
  signed_documents?: string[];
}

export const ReviewStep: React.FC<WizardStepProps> = ({
  sessionState,
  onNext,
  onBack,
}) => {
  // Get data from all steps with proper typing
  const personalDetails = (sessionState.step_data_by_type['personal_details'] || {}) as PersonalDetailsData;
  const employmentDetails = (sessionState.step_data_by_type['employment_type'] || {}) as EmploymentDetailsData;
  const taxInfo = (sessionState.step_data_by_type['tax_forms'] || {}) as TaxInfoData;
  const documents = (sessionState.step_data_by_type['document_signing'] || {}) as DocumentsData;

  const sections = [
    {
      icon: <PersonOutlined />,
      title: 'Personal Information',
      items: [
        { label: 'Name', value: `${personalDetails.first_name || ''} ${personalDetails.last_name || ''}`.trim() || 'Not provided' },
        { label: 'Phone', value: personalDetails.phone || 'Not provided' },
        { label: 'Date of Birth', value: personalDetails.date_of_birth || 'Not provided' },
        { label: 'Address', value: personalDetails.address_line_1 ?
          `${personalDetails.address_line_1}${personalDetails.address_line_2 ? ', ' + personalDetails.address_line_2 : ''}, ${personalDetails.city}, ${personalDetails.state} ${personalDetails.zip_code}`
          : 'Not provided' },
        { label: 'Emergency Contact', value: personalDetails.emergency_contact_name || 'Not provided' },
      ],
    },
    {
      icon: <WorkOutlined />,
      title: 'Employment Details',
      items: [
        { label: 'Employment Type', value: sessionState.member_type === 'w2' ? 'W-2 Employee' : '1099 Contractor' },
        { label: 'Job Title', value: employmentDetails.job_title || 'Not provided' },
        { label: 'Department', value: employmentDetails.department || 'Not provided' },
        { label: 'Start Date', value: employmentDetails.start_date || 'Not provided' },
        { label: 'Direct Deposit', value: employmentDetails.has_direct_deposit ? 'Yes' : 'No' },
      ],
    },
    {
      icon: <AccountBalanceOutlined />,
      title: 'Tax Information',
      items: sessionState.member_type === 'w2'
        ? [
            { label: 'Filing Status', value: formatFilingStatus(taxInfo.filing_status) },
            { label: 'Dependents Credit', value: taxInfo.dependents_amount ? `$${taxInfo.dependents_amount}` : 'None' },
            { label: 'Extra Withholding', value: taxInfo.extra_withholding ? `$${taxInfo.extra_withholding}` : 'None' },
          ]
        : [
            { label: 'Tax Classification', value: formatTaxClassification(taxInfo.tax_classification) },
            { label: 'TIN Type', value: taxInfo.tin_type === 'ssn' ? 'SSN' : 'EIN' },
          ],
    },
    {
      icon: <DescriptionOutlined />,
      title: 'Documents',
      items: [
        {
          label: 'Documents',
          value: `${(documents.signed_documents || []).length} acknowledged`,
        },
      ],
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h4" sx={{ mb: 1 }}>
        Review Your Information
      </Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
        Please review all the information you've provided before submitting your onboarding.
      </Typography>

      <Alert color="primary" variant="soft" sx={{ mb: 3 }}>
        Make sure all information is correct. You can go back to previous steps to make changes if needed.
      </Alert>

      {sections.map((section, index) => (
        <Card key={section.title} variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ color: 'var(--brand-text, #7013f5)' }}>{section.icon}</Box>
              <Typography level="title-md">{section.title}</Typography>
            </Box>
            <List size="sm">
              {section.items.map((item) => (
                <ListItem key={item.label} sx={{ py: 0.5 }}>
                  <ListItemContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography level="body-sm" color="neutral">
                        {item.label}
                      </Typography>
                      <Typography level="body-sm" fontWeight="md">
                        {item.value}
                      </Typography>
                    </Box>
                  </ListItemContent>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      ))}

      <Divider sx={{ my: 3 }} />

      {/* Agreement */}
      <Card variant="soft" color="primary" sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-sm" sx={{ mb: 1 }}>
            Submission Agreement
          </Typography>
          <Typography level="body-sm">
            By clicking "Submit Onboarding", I certify that:
          </Typography>
          <List size="sm" marker="disc" sx={{ pl: 2 }}>
            <ListItem>
              <Typography level="body-sm">
                All information I have provided is true and accurate to the best of my knowledge.
              </Typography>
            </ListItem>
            <ListItem>
              <Typography level="body-sm">
                I understand that providing false information may result in termination of employment.
              </Typography>
            </ListItem>
            <ListItem>
              <Typography level="body-sm">
                I have reviewed and agree to all signed documents.
              </Typography>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" color="neutral" onClick={onBack}>
          Back
        </Button>
        <Button color="success" startDecorator={<CheckCircleOutlined />} onClick={onNext}>
          Submit Onboarding
        </Button>
      </Box>
    </Box>
  );
};

function formatFilingStatus(status: string | undefined): string {
  const statusMap: Record<string, string> = {
    single: 'Single',
    married_filing_jointly: 'Married Filing Jointly',
    married_filing_separately: 'Married Filing Separately',
    head_of_household: 'Head of Household',
  };
  return statusMap[status || ''] || 'Not provided';
}

function formatTaxClassification(classification: string | undefined): string {
  const classificationMap: Record<string, string> = {
    individual: 'Individual/Sole Proprietor',
    sole_proprietor: 'Sole Proprietor',
    llc: 'LLC',
    c_corp: 'C Corporation',
    s_corp: 'S Corporation',
    partnership: 'Partnership',
    trust: 'Trust/Estate',
  };
  return classificationMap[classification || ''] || 'Not provided';
}

export default ReviewStep;
